/* =====================================================================
 * Avatar — 공통 아바타 스타일 & 컴포넌트
 * ===================================================================== */

import React from 'react';
import styled from 'styled-components';
import { User } from 'lucide-react';
import { resolveWorkspaceModeAvatar, useTenant } from '@auw/tenant';

/* ---- 공통 squircle 스타일 토큰 ---- */

export const avatarSquircle = {
  borderRadius: '22%',
  bgLight: 'linear-gradient(145deg, #eaeaed, #e1e1e5)',
  bgDark: 'linear-gradient(145deg, #434345, #38383a)',
  colorLight: '#4a4a4e',
  colorDark: '#c8c8cc',
  shadowLight: '0 1px 4px rgba(0,0,0,0.08)',
  shadowDark: '0 1px 4px rgba(0,0,0,0.35)',
} as const;

/* ---- 범용 Squircle 아바타 래퍼 ---- */

export const SquircleWrap = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  flex-shrink: 0;
`;

export const SquircleCircle = styled.span<{ $size: number; $bg?: string }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${avatarSquircle.borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $bg }) =>
    $bg ?? (theme.mode === 'dark' ? avatarSquircle.bgDark : avatarSquircle.bgLight)};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? avatarSquircle.shadowDark : avatarSquircle.shadowLight};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? avatarSquircle.colorDark : avatarSquircle.colorLight};
  font-weight: 700;
  font-size: ${({ $size }) => Math.round($size * 0.42)}px;
  line-height: 1;
  user-select: none;
  overflow: hidden;

  svg {
    color: inherit;
    flex-shrink: 0;
  }
`;

export const SquircleImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

/* ---- 레거시 호환: 채팅 말풍선용 (기존 Avatar 인터페이스) ---- */

interface AvatarProps {
  role: 'user' | 'assistant';
  size?: number;
}

const UserWrapper = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  border-radius: ${avatarSquircle.borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.textInverse};
  flex-shrink: 0;
  transition: background ${({ theme }) => theme.transitions.fast};

  svg {
    width: ${({ $size }) => Math.round($size * 0.5)}px;
    height: ${({ $size }) => Math.round($size * 0.5)}px;
  }
`;

const AgentLogoWrapper = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  border-radius: ${avatarSquircle.borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  flex-shrink: 0;
  overflow: hidden;
`;

const AgentLogo = styled.img<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  object-fit: contain;
  display: block;
`;

export const Avatar: React.FC<AvatarProps> = ({ role, size = 32 }) => {
  /* Resolution policy for the assistant message avatar:
   *   1. `assets.workspaceModes.agent.avatar` — opt-in branded chip
   *      that some tenants (e.g. samsung's `agent-bubble.png`) ship
   *      specifically for the agent surface.
   *   2. `assets.agentLogo` — every manifest is REQUIRED to declare
   *      this slot, so it's a guaranteed fallback. B2C lands here
   *      with `/agent_logo.png`, preserving byte-perfect parity with
   *      the legacy hard-coded path.
   * This avoids the previous hard-coded `/agent_logo.png` that 404'd
   * on enterprise tenants whose publicDir doesn't ship that file. */
  const tenant = useTenant();
  if (role === 'assistant') {
    const modeAvatar = resolveWorkspaceModeAvatar(tenant, 'agent');
    const src = modeAvatar?.src ?? tenant.assets.agentLogo;
    const alt = modeAvatar?.alt ?? `${tenant.identity.appName} agent`;
    return (
      <AgentLogoWrapper $size={size}>
        <AgentLogo src={src} alt={alt} $size={size} />
      </AgentLogoWrapper>
    );
  }
  return (
    <UserWrapper $size={size}>
      <User />
    </UserWrapper>
  );
};
